import { useMemo, useState } from "react";

import { useAccount } from "../account.js";
import {
  AUTO_SEND_DELAY_MAX,
  AUTO_SEND_DELAY_MIN,
  AUTO_SEND_DELAY_STEP,
  DEFAULT_AUTO_SEND_DELAY,
  DICTATION_LANGUAGES,
  findDictation,
  setDictationAutoSend,
  setDictationAutoSendDelay,
  setDictationLanguage,
  useDictationSettings,
  type DictationLanguage,
  type DictationWindow,
} from "../dictation.js";
import { LOCALES, setLocale, useLocale, useText, type Locale } from "../i18n.js";
import { SETTINGS } from "../text/settings.js";
import { setStyle, setTheme, useStyle, useTheme, type Style, type Theme } from "../theme.js";
import { deviceTimeZone, timeZoneChoices, useTimeZone, zoneLabel } from "../time-zone.js";

import { AccountFace } from "./AccountMenu.js";
import { Switch } from "./AgentSettings.js";
import { useConfirm } from "./ConfirmDialog.js";
import { ReviewSettings } from "./ReviewSettings.js";
import { WorkspaceSkills } from "./WorkspaceSkills.js";
import { ActiveGlide } from "./ActiveGlide.js";

export type SettingsPage = "general" | "machine" | "permissions" | "skills";

type Words = (typeof SETTINGS)["en"];

const PAGES: ReadonlyArray<{ readonly id: SettingsPage; readonly label: (t: Words) => string }> = [
  { id: "general", label: (t) => t.pageGeneral },
  { id: "machine", label: (t) => t.pageMachine },
  { id: "permissions", label: (t) => t.pagePermissions },
  { id: "skills", label: (t) => t.pageSkills },
];

const message = (cause: unknown): string => (cause instanceof Error ? cause.message : String(cause));

const THEMES: ReadonlyArray<{ readonly theme: Theme; readonly label: (t: Words) => string }> = [
  { theme: "dark", label: (t) => t.themeDark },
  { theme: "light", label: (t) => t.themeLight },
];

/** Every style, in the order Settings shows them; the featured ones are on show before "More styles" is pressed. */
const STYLES: ReadonlyArray<{ readonly style: Style; readonly label: (t: Words) => string; readonly about: (t: Words) => string; readonly featured?: true }> = [
  { style: "calm", label: (t) => t.styleCalm, about: (t) => t.styleCalmAbout, featured: true },
  { style: "tactical", label: (t) => t.styleTactical, about: (t) => t.styleTacticalAbout, featured: true },
  { style: "industrial", label: (t) => t.styleIndustrial, about: (t) => t.styleIndustrialAbout },
  { style: "vintage", label: (t) => t.styleVintage, about: (t) => t.styleVintageAbout },
  { style: "stellar", label: (t) => t.styleStellar, about: (t) => t.styleStellarAbout },
  { style: "terminal", label: (t) => t.styleTerminal, about: (t) => t.styleTerminalAbout },
  { style: "studio", label: (t) => t.styleStudio, about: (t) => t.styleStudioAbout },
  { style: "glass", label: (t) => t.styleGlass, about: (t) => t.styleGlassAbout },
];


/**
 * A small picture of the app in one theme and style. It carries both choices
 * on itself, so the stylesheet draws it from that theme's own values
 * (styles/tokens.css) whatever the page around it is in.
 */
function Preview({ theme, style }: { readonly theme: Theme; readonly style: Style }): React.JSX.Element {
  const t = useText(SETTINGS);
  return (
    <span className="theme-preview" data-theme={theme} data-style={style} aria-hidden="true">
      <span className="theme-preview-side">
        <b>{t.previewBots}</b>
        <i />
        <i />
        <i />
      </span>
      <span className="theme-preview-chat">
        <i />
        <i />
        <i />
      </span>
    </span>
  );
}

/** One choice among a few tiles, each a picture of the app with that choice made. */
function Choices<T extends string>({ name, label, value, options, preview, onChoose, fallback }: { readonly name: string; readonly label: string; readonly value: T; readonly options: ReadonlyArray<{ readonly id: T; readonly label: string }>; readonly preview: (id: T) => React.JSX.Element; readonly onChoose: (id: T) => void; readonly fallback: T }): React.JSX.Element {
  const t = useText(SETTINGS);
  return (
    <div className="theme-choices" role="radiogroup" aria-label={label}>
      {options.map((one) => (
        <label key={one.id} className={`theme-choice${value === one.id ? " chosen" : ""}`}>
          <input type="radio" name={name} value={one.id} checked={value === one.id} onChange={() => onChoose(one.id)} />
          {preview(one.id)}
          <span className="theme-name">
            {one.label}
            {one.id === fallback ? <span className="theme-default">{t.defaultChoice}</span> : null}
          </span>
        </label>
      ))}
    </div>
  );
}

/** Dark or light, and one of the styles, each shown as a small picture of the app in it; the choices are this device's (theme.ts). */
function Appearance(): React.JSX.Element {
  const t = useText(SETTINGS);
  const theme = useTheme();
  const style = useStyle();
  const [more, setMore] = useState(false);
  // Folded, the row shows the featured styles and whichever one this device is in.
  const shown = more ? STYLES : STYLES.filter((one) => one.featured === true || one.style === style);
  const hidden = STYLES.length - shown.length;
  const chosen = STYLES.find((one) => one.style === style) ?? STYLES[0]!;
  return (
    <section className="settings-section">
      <h4>{t.appearance}</h4>
      <div className="settings-row theme-row">
        <div className="settings-row-text">
          <strong>{t.theme}</strong>
          <span className="muted">{t.themeAbout}</span>
        </div>
        <Choices name="theme" label={t.theme} value={theme} fallback="dark" options={THEMES.map((one) => ({ id: one.theme, label: one.label(t) }))} preview={(id) => <Preview theme={id} style={style} />} onChoose={setTheme} />
      </div>
      <div className="settings-row theme-row">
        <div className="settings-row-text">
          <strong>{t.style}</strong>
          <span className="muted">{t.styleAbout}</span>
        </div>
        <Choices name="style" label={t.style} value={style} fallback="calm" options={shown.map((one) => ({ id: one.style, label: one.label(t) }))} preview={(id) => <Preview theme={theme} style={id} />} onChoose={setStyle} />
        <div className="theme-foot">
          <span className="muted">
            {chosen.label(t)}: {chosen.about(t)}
          </span>
          {more || hidden > 0 ? (
            <button type="button" className="linkish" aria-expanded={more} onClick={() => setMore((open) => !open)}>
              {more ? t.fewerStyles : t.moreStyles(hidden)}
            </button>
          ) : null}
        </div>
      </div>
    </section>
  );
}

/**
 * The app's language, this device's (i18n.ts). Each name is in its own
 * language, so someone who cannot read the one on screen still finds theirs.
 */
function Language(): React.JSX.Element {
  const t = useText(SETTINGS);
  const current = useLocale();
  return (
    <section className="settings-section">
      <h4>{t.language}</h4>
      <div className="settings-row">
        <div className="settings-row-text">
          <strong>{t.appLanguage}</strong>
          <span className="muted">{t.appLanguageAbout}</span>
        </div>
        <select className="settings-select" value={current} onChange={(event) => setLocale(event.target.value as Locale)} aria-label={t.appLanguage}>
          {LOCALES.map((one) => (
            <option key={one.id} value={one.id} lang={one.id}>
              {one.name}
            </option>
          ))}
        </select>
      </div>
    </section>
  );
}

/**
 * The mic in the message box (dictation.ts): the language it listens for and
 * whether it sends when you stop, the same two choices the listening bar
 * offers, and here only, how long a silence counts as stopping. All this
 * device's.
 */
function Dictation(): React.JSX.Element {
  const t = useText(SETTINGS);
  const { language, autoSend, autoSendDelay } = useDictationSettings();
  // "1 second", "1.5 seconds": a delay as the slider shows it.
  const seconds = (ms: number): string => t.seconds(ms / 1000);
  const found = useMemo(() => (typeof window === "undefined" ? null : findDictation(window as unknown as DictationWindow)), []);
  const available = found != null;
  // On Android (the app, or Chrome there) the phone decides when speaking has finished, and the wait is not used.
  const phoneDecides = found != null && !found.listensThroughPauses;
  return (
    <section className="settings-section">
      <h4>{t.dictation}</h4>
      <div className="settings-row">
        <div className="settings-row-text">
          <strong>{t.dictationLanguage}</strong>
          <span className="muted">{t.dictationLanguageAbout}</span>
        </div>
        <select className="settings-select" value={language} onChange={(event) => setDictationLanguage(event.target.value as DictationLanguage)} aria-label={t.dictationLanguageLabel}>
          {DICTATION_LANGUAGES.map((entry) => (
            <option key={entry.tag} value={entry.tag}>
              {entry.name}
            </option>
          ))}
        </select>
      </div>
      <div className="settings-row">
        <div className="settings-row-text">
          <strong>{t.autoSend}</strong>
          <span className="muted">{t.autoSendAbout}</span>
        </div>
        <Switch checked={autoSend} onChange={setDictationAutoSend} label={t.autoSend} />
      </div>
      <div className="settings-row">
        <div className="settings-row-text">
          <strong>{t.wait}</strong>
          <span className="muted">{t.waitAbout}</span>
          <span className={`muted settings-row-caveat${phoneDecides ? " applies" : ""}`}>{phoneDecides ? t.waitNotHere : t.waitNotOnAndroid}</span>
        </div>
        <label className="settings-slider">
          <span className="settings-slider-value">
            {seconds(autoSendDelay)}
            {autoSendDelay === DEFAULT_AUTO_SEND_DELAY ? <span className="muted">{t.defaultMark}</span> : null}
          </span>
          <input
            type="range"
            min={AUTO_SEND_DELAY_MIN}
            max={AUTO_SEND_DELAY_MAX}
            step={AUTO_SEND_DELAY_STEP}
            value={autoSendDelay}
            disabled={!autoSend}
            onChange={(event) => setDictationAutoSendDelay(Number(event.target.value))}
            aria-label={t.wait}
            aria-valuetext={seconds(autoSendDelay)}
          />
        </label>
      </div>
      <p className="muted settings-note">
        {available ? t.dictationAvailable : t.dictationUnavailable}
      </p>
    </section>
  );
}

/** The account, how the app looks, the zone every time is shown in and the bots work to, and the tour to take again. */
function General({ onTour }: { readonly onTour: (() => void) | undefined }): React.JSX.Element {
  const t = useText(SETTINGS);
  const account = useAccount();
  const { zone, chosen, choose } = useTimeZone();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const device = deviceTimeZone();
  // Some four hundred zones, each labelled with its offset: worked out once per zone shown, not on every keystroke.
  const choices = useMemo(() => timeZoneChoices(zone, device).map((one) => ({ zone: one, label: zoneLabel(one) })), [zone, device]);
  const username = account?.session.account?.username ?? "";

  const pick = (next: string): void => {
    setSaving(true);
    setError(null);
    choose(next)
      .catch((cause: unknown) => setError(message(cause)))
      .finally(() => setSaving(false));
  };

  return (
    <>
      <section className="settings-section">
        <h4>{t.account}</h4>
        {account != null ? (
          <div className="settings-row">
            <AccountFace name={username} size={36} />
            <div className="settings-row-text">
              <strong>{username}</strong>
              <span className="muted">{t.accountAbout}</span>
            </div>
            <button type="button" onClick={() => void account.logout().catch((cause: unknown) => setError(message(cause)))}>
              {t.logOut}
            </button>
          </div>
        ) : (
          <div className="settings-row">
            <AccountFace name={null} size={36} />
            <div className="settings-row-text">
              <strong>{t.thisComputer}</strong>
              <span className="muted">{t.thisComputerAbout}</span>
            </div>
          </div>
        )}
      </section>
      <Language />
      <Appearance />
      <section className="settings-section">
        <h4>{t.timeZone}</h4>
        <div className="settings-row">
          <div className="settings-row-text">
            <strong>{t.yourTimeZone}</strong>
            <span className="muted">{t.timeZoneAbout}</span>
          </div>
          <select className="settings-select" value={zone} disabled={saving} onChange={(event) => pick(event.target.value)} aria-label={t.timeZone}>
            {choices.map((one) => (
              <option key={one.zone} value={one.zone}>
                {one.label}
              </option>
            ))}
          </select>
        </div>
        {chosen == null ? (
          <p className="muted settings-note">
            {t.timeZoneUnset}{" "}
            <button type="button" className="linkish" disabled={saving} onClick={() => pick(device)}>
              {t.useZone(device.replace(/_/g, " "))}
            </button>
          </p>
        ) : null}
      </section>
      <Dictation />
      {onTour != null ? (
        <section className="settings-section">
          <h4>{t.gettingStarted}</h4>
          <div className="settings-row">
            <div className="settings-row-text">
              <strong>{t.tour}</strong>
              <span className="muted">{t.tourAbout}</span>
            </div>
            <button type="button" onClick={onTour}>
              {t.takeTour}
            </button>
          </div>
        </section>
      ) : null}
      {error != null ? <div className="settings-error">{error}</div> : null}
    </>
  );
}

/** The workspace's machine: for now, a restart. */
function Machine(): React.JSX.Element {
  const t = useText(SETTINGS);
  const account = useAccount();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const confirm = useConfirm();

  const restart = async (): Promise<void> => {
    if (account == null || !(await confirm({ title: t.restartConfirmTitle, message: t.restartConfirmMessage, action: t.restart }))) return;
    setBusy(true);
    setError(null);
    // The page goes to the restart screen as soon as the service answers; this form goes with it.
    account.restart().catch((cause: unknown) => {
      setError(message(cause));
      setBusy(false);
    });
  };

  return (
    <>
      <section className="settings-section">
        <h4>{t.machine}</h4>
        <div className="settings-row">
          <div className="settings-row-text">
            <strong>{t.restart}</strong>
            <span className="muted">{t.restartAbout}</span>
          </div>
          <button type="button" disabled={account == null || busy} onClick={() => void restart()}>
            {busy ? t.restarting : t.restart}
          </button>
        </div>
        {account == null ? (
          <p className="muted settings-note">
            {t.restartLocalBefore}
            <code>npm run box:down</code>
            {t.restartLocalBetween}
            <code>npm run box:up</code>
            {t.restartLocalAfter}
          </p>
        ) : null}
      </section>
      {error != null ? <div className="settings-error">{error}</div> : null}
    </>
  );
}

/**
 * Settings, as the prototype lays its own out: the pages down the side, the
 * open one beside them. General holds the account, the theme, the time zone, dictation and the tour; Virtual
 * Machine the restart; Permissions the checks run before an action; Skills every
 * written procedure, shared and per bot.
 */
export function Settings({ initialPage = "general", onClose, onTour }: { readonly initialPage?: SettingsPage; readonly onClose: () => void; readonly onTour?: () => void }): React.JSX.Element {
  const t = useText(SETTINGS);
  const [page, setPage] = useState<SettingsPage>(initialPage);
  const found = PAGES.find((one) => one.id === page);
  const title = found == null ? "" : found.label(t);
  return (
    <div className="settings-dialog">
      <nav className="settings-nav" aria-label={t.pagesLabel}>
        <h2>{t.title}</h2>
        <div role="tablist" aria-orientation="vertical" className="settings-tabs">
          <ActiveGlide selector='[aria-selected="true"]' className="glide-row glide-tab" />
          {PAGES.map((one) => (
            <button key={one.id} type="button" role="tab" id={`settings-tab-${one.id}`} aria-selected={page === one.id} aria-controls="settings-page" className="settings-tab" onClick={() => setPage(one.id)}>
              {one.label(t)}
            </button>
          ))}
        </div>
      </nav>
      <div className="settings-page" id="settings-page" role="tabpanel" aria-labelledby={`settings-tab-${page}`}>
        <header className="settings-page-head">
          <h3>{title}</h3>
          <button type="button" className="icon" onClick={onClose} aria-label={t.closeSettings} title={t.close}>
            ×
          </button>
        </header>
        <div className="settings-page-body">{page === "general" ? <General onTour={onTour} /> : page === "machine" ? <Machine /> : page === "skills" ? <WorkspaceSkills /> : <ReviewSettings />}</div>
      </div>
    </div>
  );
}
